import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "../../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../../components/Containers/ContainerModalActions";
import CardMedia from "../../../../components/Media/CardMedia";
import CreatableAutocompleteField from "../../../../components/Form/CreatableAutocompleteField";
import Button from "../../../../components/Buttons/Button";
import Switch from "../../../../components/Form/Switch";
import Typography from "@mui/material/Typography";
import {
  MODAL_ADDITIONAL_ASSET_TITLE,
  BUTTON_CANCEL,
  BUTTON_SAVE,
  BUTTON_CHANGE_ADDITIONAL_ASSET_IMAGE,
  SECTION_ADDITIONAL_ASSETS_PROMPT_LABEL,
  SECTION_ADDITIONAL_ASSETS_PROMPT_PLACEHOLDER,
  SECTION_ADDITIONAL_ASSETS_PROMPT_REQUIRED,
  SECTION_ADDITIONAL_ASSETS_IS_CONDITIONAL_LABEL,
  SECTION_ADDITIONAL_ASSETS_IS_CONDITIONAL_HELP,
  SECTION_ADDITIONAL_ASSETS_VARIABLE_INFO_LABEL,
  SECTION_ADDITIONAL_ASSETS_VARIABLE_INFO_HELP,
  SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_LABEL,
  SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_HELP,
  SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_PLACEHOLDER,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import BrandMultilineField from "./BrandMultilineField";

const PROMPT_MAX_CHARACTERS = 500;

export const ModalAdditionalAsset = ({
  modalOpen,
  onCloseModal,
  urlImage = "",
  prompt: initialPrompt = "",
  isConditional: initialIsConditional = false,
  variableInfo: initialVariableInfo = false,
  variantGroup: initialVariantGroup = "",
  variantGroupOptions = [],
  hasAdvancedConfigurationAdditionalAssets = false,
  onSave,
  onChangeImage,
  isUploading = false,
}) => {
  const { t } = useTranslationApp();
  const {
    control,
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      prompt: initialPrompt || "",
      isConditional: !!initialIsConditional,
      variableInfo: !!initialVariableInfo,
      variantGroup: initialVariantGroup || "",
    },
  });

  useEffect(() => {
    if (modalOpen) {
      reset({
        prompt: initialPrompt || "",
        isConditional: !!initialIsConditional,
        variableInfo: !!initialVariableInfo,
        variantGroup: initialVariantGroup || "",
      });
    }
  }, [
    modalOpen,
    initialPrompt,
    initialIsConditional,
    initialVariableInfo,
    initialVariantGroup,
    reset,
  ]);

  const isConditional = watch("isConditional");

  const onSaveAsset = ({
    prompt = "",
    isConditional: conditionalValue = false,
    variableInfo = false,
    variantGroup = "",
  }) => {
    if (!urlImage?.trim()) return;
    const resolvedIsConditional = hasAdvancedConfigurationAdditionalAssets
      ? !!conditionalValue
      : false;
    onSave?.({
      urlImage: urlImage.trim(),
      prompt: prompt.trim(),
      isConditional: resolvedIsConditional,
      variableInfo: hasAdvancedConfigurationAdditionalAssets
        ? !!variableInfo
        : false,
      variantGroup:
        hasAdvancedConfigurationAdditionalAssets && resolvedIsConditional
          ? (variantGroup || "").trim()
          : "",
    });
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={onCloseModal}
      title={t(MODAL_ADDITIONAL_ASSET_TITLE)}
      maxWidth="sm"
      fullWidth
      enableHistoryManagement={false}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        {urlImage && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CardMedia
                url={urlImage}
                sx={{
                  objectFit: "contain",
                  height: "100%",
                  width: "auto",
                  borderRadius: 2,
                  backgroundColor: "action.hover",
                }}
              />
            </Box>
            {onChangeImage && (
              <Button
                color="secondary"
                fullWidth
                onClick={onChangeImage}
                loading={isUploading}
              >
                {t(BUTTON_CHANGE_ADDITIONAL_ASSET_IMAGE)}
              </Button>
            )}
          </Box>
        )}
        <BrandMultilineField
          control={control}
          name="prompt"
          label={t(SECTION_ADDITIONAL_ASSETS_PROMPT_LABEL)}
          placeholder={t(SECTION_ADDITIONAL_ASSETS_PROMPT_PLACEHOLDER)}
          autoFocus
          showCounterCharacters
          maxCharacters={PROMPT_MAX_CHARACTERS}
          rules={{
            required: {
              value: true,
              message: t(SECTION_ADDITIONAL_ASSETS_PROMPT_REQUIRED),
            },
          }}
          errors={errors}
        />

        {hasAdvancedConfigurationAdditionalAssets && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Controller
              control={control}
              name="isConditional"
              render={({ field: { value, onChange } }) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(SECTION_ADDITIONAL_ASSETS_IS_CONDITIONAL_LABEL)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t(SECTION_ADDITIONAL_ASSETS_IS_CONDITIONAL_HELP)}
                    </Typography>
                  </Box>
                  <Switch
                    id="additional-asset-is-conditional"
                    isActive={!!value}
                    hasLabel={false}
                    onChangeIsActive={(nextValue) => {
                      onChange(nextValue);
                      if (!nextValue) {
                        setValue("variantGroup", "");
                      }
                    }}
                  />
                </Box>
              )}
            />

            <Controller
              control={control}
              name="variableInfo"
              render={({ field: { value, onChange } }) => (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {t(SECTION_ADDITIONAL_ASSETS_VARIABLE_INFO_LABEL)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "text.secondary" }}
                    >
                      {t(SECTION_ADDITIONAL_ASSETS_VARIABLE_INFO_HELP)}
                    </Typography>
                  </Box>
                  <Switch
                    id="additional-asset-variable-info"
                    isActive={!!value}
                    hasLabel={false}
                    onChangeIsActive={onChange}
                  />
                </Box>
              )}
            />

            {isConditional && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {t(SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_LABEL)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {t(SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_HELP)}
                </Typography>
                <CreatableAutocompleteField
                  control={control}
                  name="variantGroup"
                  placeholder={t(
                    SECTION_ADDITIONAL_ASSETS_VARIANT_GROUP_PLACEHOLDER
                  )}
                  options={variantGroupOptions}
                  errors={errors}
                />
              </Box>
            )}
          </Box>
        )}
      </Box>
      <DialogActions sx={{ p: 0, pt: 2 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onCloseModal,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_SAVE,
            onClick: handleSubmit(onSaveAsset),
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ModalAdditionalAsset;
